import {driver} from 'driver.js';
import {hintsApi} from '@/base/hints/hintsApi';

export const hintConfigs = {
    'select-current-np': () => import('@/portal/modules/OnboardingUserHints/selectCurrentNpHintConfig'),
    'route-builder-guide': () => import('@route-builder/modules/routeBuilderHintConfig'),
};

const activeHints = new Set();

export function initHint(key, {store, force}) {
    return runHint(key, {store, force});
}

async function runHint(key, {store, force} = {force: false}) {
    const loader = hintConfigs[key];
    if (!loader) {
        console.warn(`Конфигурация подсказки ${key} не найдена.`);
        return;
    }

    const module = await loader();
    const config = module?.default;
    if (!config) {
        console.warn(`Конфигурация подсказки ${key} не загружена.`);
        return;
    }

    const user = getUserFromStore(store);
    const storedVersion = getHintVersion(user, config.key);
    if (!force && storedVersion >= config.version) {
        return;
    }

    const retryDelay = config.retryDelay ?? 300;
    const retryLimit = config.retryLimit ?? 20;
    waitForHints(config.steps, () => {
        startGuide(config, {store, user, force});
    }, retryDelay, retryLimit);
}

function waitForHints(steps, onReady, retryDelay, retryLimit, attempt = 0) {
    if (hasAvailableTargets(steps)) {
        onReady();
        return;
    }

    if (attempt >= retryLimit) {
        return;
    }

    setTimeout(() => {
        waitForHints(steps, onReady, retryDelay, retryLimit, attempt + 1);
    }, retryDelay);
}

function hasAvailableTargets(steps) {
    return steps.some((step) => document.querySelector(step.selector));
}

function startGuide(config, {store, user, force}) {
    if (activeHints.has(config.key) && !force) {
        return;
    }

    const steps = config.steps
        .filter((step) => document.querySelector(step.selector))
        .map((step) => ({
            element: step.selector,
            popover: {
                description: step.description,
                side: step.side,
                align: step.align,
            },
        }));

    if (!steps.length) {
        return;
    }

    activeHints.add(config.key);

    let wasMarked = false;
    const markSeenOnce = () => {
        if (wasMarked) {
            return;
        }
        wasMarked = true;
        markHintSeen(config, user);
        if (store) {
            saveHintVersionToStore(store, config.key, config.version);
        }
    };

    let driverInstance;
    const externalNext = config.driverOptions?.onNextClick;
    const driverOptions = {
        showButtons: ['next', 'previous', 'close'],
        showProgress: true,
        disableActiveInteraction: true,
        nextBtnText: 'Дальше',
        prevBtnText: 'Назад',
        doneBtnText: 'Понятно',
        closeBtnText: 'Закрыть',
        ...config.driverOptions,
        onCloseClick: () => {
            markSeenOnce();
            driverInstance.destroy();
        },
        onDestroyed: () => {
            markSeenOnce();
            activeHints.delete(config.key);
        },
        onNextClick: () => {
            if (externalNext) {
                externalNext({driverInstance, markSeen: markSeenOnce});
            }
        },
        steps,
    };

    if (externalNext) {
        driverOptions.onNextClick = () => {
            externalNext({
                driverInstance,
                markSeen: markSeenOnce,
                next: () => driverInstance.moveNext(),
            });
        };
    } else {
        delete driverOptions.onNextClick;
    }

    driverInstance = driver(driverOptions);

    driverInstance.drive();
}

function markHintSeen(config, user) {
    if (user) {
        saveHintVersionToUser(user, config.key, config.version);
    }
    hintsApi.markSeen(config.key, config.version).catch((error) => {
        console.error('Не удалось сохранить подсказку', error);
    });
}

function getUserFromStore(store) {
    if (!store) {
        return null;
    }
    if (store.getters?.['user/user']) {
        return store.getters['user/user'];
    }
    if (store.getters?.user) {
        return store.getters.user;
    }
    return store.state?.user?.user ?? null;
}

function getHintVersion(user, key) {
    if (!user) {
        return 0;
    }
    const hints = user.hints
        ?? user.client?.options?.hints
        ?? user.options?.hints
        ?? {};
    return Number(hints[key] ?? 0);
}

function saveHintVersionToStore(store, key, version) {
    const user = store.state?.user?.user;
    if (!user) {
        return;
    }
    saveHintVersionToUser(user, key, version);
}

function saveHintVersionToUser(user, key, version) {
    if (user.hints !== undefined) {
        user.hints = user.hints ?? {};
        user.hints[key] = version;
        return;
    }
    if (user.client?.options) {
        user.client.options.hints = user.client.options.hints ?? {};
        user.client.options.hints[key] = version;
        return;
    }
    if (user.options) {
        user.options.hints = user.options.hints ?? {};
        user.options.hints[key] = version;
        return;
    }
    user.hints = {[key]: version};
}
